/* eslint-disable jsx-a11y/control-has-associated-label */
/* eslint-disable react/no-unstable-nested-components */

import { formatDate, formatTime, getCountryImageSource, Status } from 'src/utils/general';
import classNames from 'classnames';
import React from 'react';
import { InboundStatusBadge } from 'src/components/tables/InboundTable/InboundTable';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { QueryClient, useQueryClient } from '@tanstack/react-query';
import Icon from 'src/components/ui/Icon/Icon';
import {
  InboundOrder,
  InboundOrderList,
  scheduleInboundRefetch,
  useInboundOrderMutations,
  useInboundOrdersQuery,
} from 'src/services/inbound';
import { setInboundNavIds } from 'src/utils/inboundNav';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import ProgressIndicator from 'src/components/ui/ProgressIndicator/ProgressIndicator';
import { useQuickFilterQuery } from 'src/services/utility';
import { ExtraUnitsBadge } from './ExtraUnitsBadge';
import styles from '../styles/Inventory.module.scss';
import { useNavigationItems } from '../../hooks/useNavigationItems';
import { DataView, DataViewAction } from '../../components/ui/TableOverview/DataView';
import NoDataPlaceholder from '../../components/layouts/NoDataPlaceholder/NoDataPlaceholder';
import { useDataViewController } from '../../hooks/useDataViewController';
import { SearchModels } from '../../components/ui/TableOverview/utils/SearchFieldRegistry';
import { showSpinnerToast, hideSpinnerToast } from '../../utils/spinnerToaster';

// Extracted so the cancel wiring (async-saga toast + delayed refetch, see
// docs/specs 2026-08-07-flowsync-frontend task 6) can be unit-tested directly
// (Inbound.test.ts) without rendering the whole DataView/InboundTable tree.
type CancelMutationLike = {
  mutateAsync: (variables: { params: { path: { model: 'purchase.order' } }; body: number[] }) => Promise<unknown>;
};

export function createInboundCancelAction({
  t,
  cancelMutation,
  queryClient,
  showInContextMenu,
}: {
  t: (key: string) => string;
  cancelMutation: CancelMutationLike;
  queryClient: QueryClient;
  showInContextMenu?: boolean;
}): DataViewAction<InboundOrderList> {
  return {
    title: t('inbound.cancel'),
    titlePlural: t('inbound.cancel_plural'),
    icon: 'close',
    // Cancel is REQUESTED at the warehouse (async saga) — the mutation only
    // confirms the request was accepted, `inbound_state` flips once the WMS
    // confirms (or stays unchanged on rejection). Schedule a delayed
    // re-invalidation on top of the immediate one (inside the mutation's
    // onSuccess) to pick this up without polling or an optimistic update.
    // eslint-disable-next-line consistent-return
    onExecute: selected => {
      const ids = selected.map(s => s.id).filter((id): id is number => id != null);
      if (ids.length > 0) {
        return cancelMutation
          .mutateAsync({
            params: { path: { model: 'purchase.order' } },
            body: ids,
          })
          .then(() => {
            scheduleInboundRefetch(queryClient);
          });
      }
    },
    // Explicit renderSuccessMessage/renderErrorMessage (rather than a manual
    // toast.success(...) call in onExecute) deliberately owns the messaging: without
    // it, DataView's executeActionAndShowToast fires an extra generic
    // "Aktion: '...' war erfolgreich!" toast whenever an action's (locale-translated)
    // title contains "abbrechen" and has no renderSuccessMessage of its own — which
    // is true for this action's German title ("Wareneingang abbrechen"), the app's
    // default locale. This config sidesteps that double-toast entirely.
    toast: {
      // the backend cancels drafts locally and REQUESTS the rest at the warehouse —
      // the bulk endpoint does not tell which, so the toast stays neutral
      renderSuccessMessage: () => t('inbound.cancel_submitted'),
      renderErrorMessage: () => t('inbound.cancel_failed'),
    },
    // Inbound-Draft spec 2026-11-03 §2.3: the backend's synchronous pre-check
    isDisabled: selected => selected.some(s => !s.is_cancelable),
    disabledHint: t('inbound.cancel_disabled_hint'),
    confirmSettings: {
      renderTitle: selected =>
        selected.length > 1 ? t('inbound.cancel_dialog_title_plural') : t('inbound.cancel_dialog_title'),
      renderContent: () => t('inbound.cancel_dialog_copy'),
      confirmLabel: t('general.confirm'),
      cancelLabel: t('general.cancel'),
    },
    isRowClickAction: false,
    showInContextMenu,
    bulkSupport: true,
  };
}

const sequentially = (ids: number[], run: (id: number) => Promise<unknown>) =>
  ids.reduce(async (acc, id) => {
    await acc;
    await run(id);
  }, Promise.resolve());

// Inbound-Draft spec 2026-11-03 §6: confirm drafts from the list (parity with the
// outbound list). Sequential and awaited — the outbound confirm forgets the await
// and always reports success; do not copy that.
export function createInboundConfirmAction({
  t,
  confirmInboundOrderAsync,
  showInContextMenu,
}: {
  t: (key: string) => string;
  confirmInboundOrderAsync: (id: number) => Promise<unknown>;
  showInContextMenu?: boolean;
}): DataViewAction<InboundOrderList> {
  return {
    title: t('inbound.confirm_cta'),
    titlePlural: t('inbound.confirm_cta_plural'),
    icon: 'checkmark',
    isDisabled: selected => selected.some(s => !s.is_editable || s.inbound_state?.key !== Status.DRAFT),
    disabledHint: t('inbound.confirm_disabled_hint'),
    onExecute: async selected => {
      const ids = selected.map(s => s.id).filter((id): id is number => id != null);
      await sequentially(ids, confirmInboundOrderAsync);
    },
    toast: {
      renderSuccessMessage: () => t('inbound.confirm_success'),
      renderErrorMessage: () => t('inbound.confirm_failed'),
    },
    confirmSettings: {
      renderTitle: selected =>
        selected.length > 1 ? t('inbound.confirm_dialog_title_plural') : t('inbound.confirm_dialog_title'),
      renderContent: () => t('inbound.confirm_dialog_copy_list'),
      confirmLabel: t('general.confirm'),
      cancelLabel: t('general.cancel'),
    },
    isRowClickAction: false,
    showInContextMenu,
    bulkSupport: true,
  };
}

// Inbound-Draft spec 2026-11-03 §4.5: a cancelled order (no pending request) goes
// back to draft — the backend refuses anything else, the guard only hides the noise.
export function createInboundDraftAction({
  t,
  setInboundOrderDraftAsync,
  showInContextMenu,
}: {
  t: (key: string) => string;
  setInboundOrderDraftAsync: (id: number) => Promise<unknown>;
  showInContextMenu?: boolean;
}): DataViewAction<InboundOrderList> {
  return {
    title: t('inbound.draft_cta'),
    titlePlural: t('inbound.draft_cta_plural'),
    icon: 'note',
    isDisabled: selected => selected.some(s => s.inbound_state?.key !== Status.CANCEL),
    disabledHint: t('inbound.draft_disabled_hint'),
    onExecute: async selected => {
      const ids = selected.map(s => s.id).filter((id): id is number => id != null);
      await sequentially(ids, setInboundOrderDraftAsync);
    },
    toast: {
      renderSuccessMessage: () => t('inbound.draft_success'),
      renderErrorMessage: () => t('inbound.draft_failed'),
    },
    confirmSettings: {
      renderTitle: selected =>
        selected.length > 1 ? t('inbound.draft_dialog_title_plural') : t('inbound.draft_dialog_title'),
      renderContent: () => t('inbound.draft_dialog_copy'),
      confirmLabel: t('general.confirm'),
      cancelLabel: t('general.cancel'),
    },
    isRowClickAction: false,
    showInContextMenu,
    bulkSupport: true,
  };
}

function Inbound() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { cancelMutation, duplicateInboundOrders, confirmInboundOrderAsync, setInboundOrderDraftAsync } =
    useInboundOrderMutations();
  const { data: quickFilters } = useQuickFilterQuery('purchase.order');

  const { findAccessRights } = useNavigationItems();
  const accessRights = findAccessRights('warehouse');

  const columnHelper = createColumnHelper<InboundOrderList>();
  const columns: ColumnDef<InboundOrderList, any>[] = [
    columnHelper.accessor(row => row.name, {
      id: 'id',
      size: 150,
      enableSorting: true,
      enableResizing: true,
      meta: {
        label: t('general.reference'),
      },
      header: ({ column }) => (
        <div className="flex items-center gap-x-12">
          <span className="typo-text-12 text-faded">{column.columnDef.meta?.label}</span>
        </div>
      ),
      cell: info => (
        <div className="flex items-center gap-x-12">
          <div className="flex flex-col gap-y-4">
            <span className="typo-text-14--bold text-full"> {info.getValue()} </span>
            <span className="typo-text-12 text-faded"> {info.row.original.partner.company_name} </span>
          </div>
        </div>
      ),
    }),
    columnHelper.accessor(row => row.partner?.name, {
      id: 'name',
      size: 250,
      enableSorting: true,
      enableResizing: true,
      meta: {
        label: t('general.partner'),
      },
      header: ({ column }) => <span className="typo-text-12 text-faded">{column.columnDef.meta?.label}</span>,
      cell: info => (
        <div className="flex flex-col gap-y-4">
          <span className="typo-text-14 text-full"> {info.getValue()} </span>
          <span className="typo-text-12 text-faded">
            {info.row.original.partner?.address?.zip_code} {info.row.original.partner?.address?.street}
          </span>
        </div>
      ),
    }),
    columnHelper.accessor(row => row.date, {
      id: 'date',
      size: 90,
      enableSorting: true,
      meta: {
        label: t('general.create_date'),
      },
      header: ({ column }) => <span className="typo-text-12 text-faded">{column.columnDef.meta?.label}</span>,
      cell: info => {
        const { date } = info.row.original;
        return (
          <div className="flex flex-col gap-y-4">
            <span className="typo-text-14 text-full truncate">{date && formatDate(date)}</span>
            <span className="typo-text-12 text-faded truncate">{date && formatTime(date)}</span>
          </div>
        );
      },
    }),
    columnHelper.accessor(row => row.date_planned, {
      id: 'date_planned',
      enableSorting: true,
      enableResizing: true,

      size: 90,
      meta: {
        label: t('inbound.date_planned'),
      },
      header: ({ column }) => <span className="typo-text-12 text-faded">{column.columnDef.meta?.label}</span>,
      cell: info => {
        const datePlanned = info.row.original.date_planned;
        return (
          <div className="flex flex-col gap-y-4">
            <span className="typo-text-14 text-full truncate">{datePlanned && formatDate(datePlanned)}</span>
            <span className="typo-text-12 text-faded truncate">{datePlanned && formatTime(datePlanned)}</span>
          </div>
        );
      },
    }),
    columnHelper.accessor(row => row.write_date, {
      id: 'write_date',
      enableSorting: true,
      enableResizing: true,

      size: 90,
      meta: {
        label: t('inbound.last_update'),
      },
      header: ({ column }) => <span className="typo-text-12 text-faded">{column.columnDef.meta?.label}</span>,
      cell: info => {
        const writeDate = info.row.original.write_date;
        return (
          <div className="flex flex-col gap-y-4">
            <span className="typo-text-14 text-full truncate">{writeDate && formatDate(writeDate)}</span>
            <span className="typo-text-12 text-faded truncate">{writeDate && formatTime(writeDate)}</span>
          </div>
        );
      },
    }),
    columnHelper.accessor(row => row.reference, {
      id: 'reference',
      size: 60,
      enableSorting: false,
      meta: {
        label: t('inbound.reference'),
      },
      header: ({ column }) => (
        <span className={(classNames(styles.cellTitle, styles.centered), 'typo-text-12 text-faded')}>
          {column.columnDef.meta?.label}
        </span>
      ),
      cell: info => {
        const value = info.getValue();
        const imageSrc = getCountryImageSource(value);
        return (
          value &&
          imageSrc && (
            <div className="flex items-center gap-x-[8px]">
              <Icon name={imageSrc || ''} />
              <span className="typo-text-14 text-full flex justify-center">{value}</span>
            </div>
          )
        );
      },
    }),
    columnHelper.accessor(row => row.warehouse_country, {
      id: 'country_code',
      size: 60,
      enableSorting: true,
      enableResizing: true,
      meta: {
        label: t('general.destination'),
      },
      header: ({ column }) => (
        <span className={(classNames(styles.cellTitle, styles.centered), 'typo-text-12 text-faded')}>
          {column.columnDef.meta?.label}
        </span>
      ),
      cell: info => {
        const value = info.getValue();
        const imageSrc = getCountryImageSource(value);
        return (
          value &&
          imageSrc && (
            <div className="flex items-center gap-x-[8px]">
              <Icon name={imageSrc || ''} />
              <span className="typo-text-14 text-full flex justify-center">{value}</span>
            </div>
          )
        );
      },
    }),
    columnHelper.accessor(row => row.total_units, {
      id: 'total_units',
      enableSorting: false,
      size: 100,
      meta: {
        label: t('inbound.amount_units'),
      },
      header: ({ column }) => (
        <span className={(classNames(styles.cellTitle, styles.centered), 'typo-text-12 text-faded')}>
          {column.columnDef.meta?.label}
        </span>
      ),
      cell: info => (
        <span className="flex items-center gap-x-8 px-8">
          <span className="typo-text-14 text-full">{info.getValue() ?? 0}</span>
          <ExtraUnitsBadge extra={info.row.original.extra_units} />
        </span>
      ),
    }),
    columnHelper.accessor(row => row.all_units, {
      id: 'package_count',
      enableSorting: false,
      size: 150,
      meta: {
        label: t('inbound.package_count'),
      },
      header: ({ column }) => (
        <span className={(classNames(styles.cellTitle, styles.centered), 'typo-text-12 text-faded')}>
          {column.columnDef.meta?.label}
        </span>
      ),
      cell: info => {
        const data = info.row.original;
        const allUnits = data.all_units;
        const doneUnits = data.done_units;
        return (
          <div className="px-8">
            <ProgressIndicator
              percentage={allUnits > 0 ? (doneUnits / allUnits) * 100 : 0}
              sum={allUnits}
              progress={doneUnits}
              type="brand"
              size="large"
            />
          </div>
        );
      },
    }),
    columnHelper.accessor(row => row.inbound_state, {
      id: 'inbound_state',
      enableSorting: true,
      enableResizing: true,

      size: 100,
      meta: {
        label: 'Status',
      },
      header: ({ column }) => (
        <span className={(classNames(styles.cellTitle, styles.centered), 'typo-text-12 text-faded')}>
          {column.columnDef.meta?.label}
        </span>
      ),
      cell: info => (
        <div className="inline-block">
          <InboundStatusBadge status={info.getValue() as InboundOrder['inbound_state']} />
        </div>
      ),
    }),
  ];

  const tabs = quickFilters?.map(quickFilter => ({
    label: quickFilter.label,
    key: quickFilter.key,
  }));

  const controller = useDataViewController();

  return tabs ? (
    <DataView<InboundOrderList>
      controller={controller}
      modelKey={SearchModels.Inbound}
      filterModelId="purchase.order"
      allowBulkActions={accessRights?.read_write}
      headline={t('navbar.inbound')}
      emptyState={<NoDataPlaceholder headline={t('general.empty_inbound')} />}
      tabs={[
        { label: t('integrations.all'), key: 'all', value: quickFilters?.map(q => q.value).reduce((a, b) => a + b, 0) },
        ...(quickFilters ?? []),
      ]}
      getDomainForTab={(tabKey, index) => (index === 0 ? '[]' : `[("inbound_state", "=", "${tabKey}")]`)}
      withSaveFilters
      actions={[
        {
          title: t('general.create'),
          menuItems: [
            {
              label: t('inbound.create_cta'),
              onClick: () => {
                navigate('/warehouse/inbound/create/data');
              },
            },
            {
              label: t('inbound.import_cta'),
              onClick: () => navigate('/warehouse/inbound/import/data'),
            },
          ],
          icon: 'plus',
          showInToolbar: accessRights?.read_write,
        },
        {
          title: t('general.view_details'),
          icon: 'edit',
          onExecute: selected => {
            if (selected.length > 0) {
              navigate(`/warehouse/inbound/${selected[0].id}`);
            }
          },
          isRowClickAction: true,
          showInContextMenu: true,
        },
        createInboundCancelAction({ t, cancelMutation, queryClient, showInContextMenu: accessRights?.read_write }),
        createInboundConfirmAction({ t, confirmInboundOrderAsync, showInContextMenu: accessRights?.read_write }),
        createInboundDraftAction({ t, setInboundOrderDraftAsync, showInContextMenu: accessRights?.read_write }),
        {
          title: t('inbound.duplicate'),
          titlePlural: t('inbound.duplicate_plural'),
          icon: 'duplicate',
          // eslint-disable-next-line consistent-return
          onExecute: selected => {
            const ids = selected.map(s => s.id).filter((id): id is number => id != null);
            if (ids.length > 0) {
              showSpinnerToast(t('inbound.duplicate_in_progress'));
              return duplicateInboundOrders(ids)
                .then(created => {
                  // a single duplicate opens its new draft right away (spec §6)
                  const copy = created.length === 1 ? created[0] : undefined;
                  if (copy?.id) navigate(`/warehouse/inbound/${copy.id}?created=1`);
                })
                .finally(() => hideSpinnerToast());
            }
          },
          toast: {
            renderSuccessMessage: () => t('inbound.duplicate_success'),
            renderErrorMessage: () => t('inbound.duplicate_failed'),
          },
          isRowClickAction: false,
          showInContextMenu: accessRights?.read_write,
          bulkSupport: true,
        },
      ]}
      kpiModel="purchase.order"
      columns={columns}
      useDataQuery={useInboundOrdersQuery}
      onDataLoaded={items => setInboundNavIds(items.map(i => i.id).filter((id): id is number => id != null))}
    />
  ) : undefined;
}

export default Inbound;
